import { useEffect, useState } from 'react';

/**
 * Central theme manager.
 *
 * Mode: 'light' (default) | 'dark' — persisted in localStorage and applied as
 * [data-theme="dark"] on <html> (matches the dark section of styles.css).
 *
 * public/index.html contains a tiny inline script that applies the stored
 * value before first paint so there is no light/dark flash on load.
 */

export const THEME_STORAGE_KEY = 'bidsig-theme';

const THEME_CHANGE_EVENT = 'bidsig-theme-change';

/* Legacy keys from the removed premium-palette test feature */
const LEGACY_PALETTE_KEYS = ['bidsig-palette-light', 'bidsig-palette-dark'];

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable (private mode) — theme still applies for session */
  }
};

export const getThemeMode = () => (safeGet(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light');

/** Sync <html> with the stored theme (and clean up legacy palette state). */
export const applyStoredTheme = () => {
  const root = document.documentElement;

  if (getThemeMode() === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }

  root.removeAttribute('data-palette-light');
  root.removeAttribute('data-palette-dark');
  LEGACY_PALETTE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) { /* ignore */ }
  });
};

export const setThemeMode = (mode) => {
  safeSet(THEME_STORAGE_KEY, mode === 'dark' ? 'dark' : 'light');
  applyStoredTheme();
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT));
};

export const toggleThemeMode = () => {
  setThemeMode(getThemeMode() === 'dark' ? 'light' : 'dark');
};

/** React hook — re-renders on theme change (including from other tabs). */
export const useTheme = () => {
  const [mode, setMode] = useState(getThemeMode);

  useEffect(() => {
    const sync = () => setMode(getThemeMode());
    window.addEventListener(THEME_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return { mode };
};
